import PropTypes from 'prop-types';
import React__default from 'react';
import { DEPRECATED_PROP_MESSAGES } from '../../constants/messages.js';
import { useControllableValue } from '../../hooks/use-controllable-value.js';
import { useId } from '../../hooks/use-id.js';
import { conditionalPropType } from '../../prop-types/conditional.prop-type.js';
import { nullishPropType } from '../../prop-types/nullish.prop-types.js';
import { deprecatedProp } from '../core/deprecated-prop.js';
import { RenderComp } from '../core/render-comp.component.js';
import { renderWithRef } from '../core/render-with-ref.js';
import { ButtonbarContext } from './buttonbar.context.js';
import { filterProps } from '../core/core.utils.js';

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
const isMultiSelect = (props)=>props.variant === 'multi-select';
const propTypes = {
    /**
   * `single-select` means using radio buttons, which are mutually exclusive
   * `multi-select` means using checkbox options, of which many can be selected at once
   * `toolbar` means using regular buttons
   */ widthVariant: deprecatedProp(PropTypes.oneOf([
        'standard',
        'full-width'
    ]), DEPRECATED_PROP_MESSAGES.width),
    width: PropTypes.oneOf([
        'standard',
        'full-width'
    ]),
    name: PropTypes.string,
    variant: PropTypes.oneOf([
        'single-select',
        'multi-select',
        'toolbar'
    ]),
    // For Single select
    selectedValue: conditionalPropType(isMultiSelect, nullishPropType, PropTypes.string),
    defaultSelectedValue: conditionalPropType(isMultiSelect, nullishPropType, PropTypes.string),
    onSelectedValueChange: conditionalPropType(isMultiSelect, nullishPropType, PropTypes.func),
    // For multi select
    selectedValues: conditionalPropType(isMultiSelect, PropTypes.arrayOf(PropTypes.string.isRequired), nullishPropType),
    defaultSelectedValues: conditionalPropType(isMultiSelect, PropTypes.arrayOf(PropTypes.string.isRequired), nullishPropType),
    onSelectedValuesChange: conditionalPropType(isMultiSelect, PropTypes.func, nullishPropType)
};
const propKeysToRemove = Object.keys(propTypes);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Buttonbar = renderWithRef('Buttonbar', propTypes, (props, ref)=>{
    const { children , variant ='single-select' , widthVariant ='standard' , width =widthVariant , selectedValue: controlledSelectedValue , defaultSelectedValue =null , onSelectedValueChange: controlledOnSelectedValueChange , selectedValues: controlledSelectedValues , defaultSelectedValues =[] , onSelectedValuesChange: controlledOnSelectedValuesChange , 'aria-label': ariaLabel , 'aria-controls': ariaControls , name: customName  } = props;
    const classNames = new Set([
        'swan-buttonbar'
    ]);
    if (width !== 'standard') classNames.add(`swan-buttonbar-${width}`);
    const name = useId(customName);
    // state for single-select
    const [selectedValue, onSelectedValueChange] = useControllableValue({
        value: controlledSelectedValue !== null && controlledSelectedValue !== void 0 ? controlledSelectedValue : undefined,
        onChange: controlledOnSelectedValueChange !== null && controlledOnSelectedValueChange !== void 0 ? controlledOnSelectedValueChange : undefined,
        defaultValue: defaultSelectedValue,
        defaultOnChange: (setValue, ...onChangeArgs)=>{
            const [newValue] = onChangeArgs;
            setValue(newValue);
        }
    });
    // state for multi-select
    const [selectedValues, onSelectedValuesChange] = useControllableValue({
        value: controlledSelectedValues !== null && controlledSelectedValues !== void 0 ? controlledSelectedValues : undefined,
        onChange: controlledOnSelectedValuesChange !== null && controlledOnSelectedValuesChange !== void 0 ? controlledOnSelectedValuesChange : undefined,
        defaultValue: defaultSelectedValues || [],
        defaultOnChange: (setValue, ...onChangeArgs)=>{
            const [newSelectedValues] = onChangeArgs;
            setValue(newSelectedValues);
        }
    });
    let roleValue = 'toolbar';
    switch(variant){
        case 'single-select':
            roleValue = 'radiogroup';
            break;
        case 'multi-select':
            roleValue = 'group';
            break;
    }
    const filteredProps = _object_spread_props(_object_spread({}, filterProps(props, null, null)), {
        role: roleValue,
        'aria-label': ariaLabel,
        'aria-controls': ariaControls
    });
    return /*#__PURE__*/ React__default.createElement(ButtonbarContext.Provider, {
        value: {
            name,
            variant,
            selectedValue,
            onSelectedValueChange,
            selectedValues,
            onSelectedValuesChange
        }
    }, /*#__PURE__*/ React__default.createElement(RenderComp, {
        root: "div",
        forwardedRef: ref,
        propKeysToRemove: propKeysToRemove,
        classNames: classNames,
        props: _object_spread({}, props, filteredProps)
    }, children));
});

export { Buttonbar };
